import { Brain, Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { calendarZone } from '../../shared/calendar';
import {
  type CardRow,
  type LearningCard,
  type LearningRating,
  type LearningSession,
  learningCards,
  learningDay,
  learningQueue,
  rateLearningCard,
} from '../../shared/learning';
import type { LocalRecord, Note } from '../../shared/model';

import { request, workspace } from '../lib/engine';
import { notePath, pagePaths, RouteLink } from '../lib/routes';
import { LearningCapture } from './LearningCapture';
import {
  ActionIconButton,
  Button,
  ButtonLink,
  Disclosure,
  IconButton,
  PageHeader,
  SearchField,
  Select,
} from './ui';

function useLearningClock() {
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(timer);
  }, []);

  return now;
}

function duration(ms: number) {
  const seconds = Math.max(0, Math.ceil(ms / 1000));

  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function LearningMorning({ records }: { records: LocalRecord[] }) {
  const now = useLearningClock(),
    day = learningDay(records, now),
    queue = learningQueue(records, now);

  return (
    <section className="detail-card learning-morning">
      <div>
        <h2>Morning recall</h2>
        <p className="muted">
          After planning your day · {queue.reviews.length} due ·{' '}
          {queue.queue.length - queue.reviews.length} new available · {duration(day.remainingMs)}{' '}
          left today
        </p>
      </div>
      <ButtonLink href={pagePaths.Learning}>
        {day.active ? 'Continue review' : 'Open Learning'}
      </ButtonLink>
    </section>
  );
}

export function LearningPage({ records, offline }: { records: LocalRecord[]; offline: boolean }) {
  const now = useLearningClock(),
    today = learningDay(records, now),
    plan = learningQueue(records, now);

  const cards = learningCards(records);
  const [capture, setCapture] = useState(false);
  const [editing, setEditing] = useState<CardRow>();
  const [available, setAvailable] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [revealed, setRevealed] = useState('');
  const [displayedId, setDisplayedId] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active');
  const [page, setPage] = useState(0);
  const locked = useRef(false);
  const active = today.active;
  const current = plan.queue.find(card => card.id === displayedId) ?? plan.queue[0];

  const revealKey = current
    ? JSON.stringify([
        current.id,
        current.value.reviews,
        current.value.question,
        current.value.answer,
      ])
    : '';

  useEffect(() => {
    setDisplayedId(current?.id ?? '');
  }, [current?.id]);

  const showingAnswer = Boolean(revealKey && revealed === revealKey);

  const matches = cards.filter(
    card =>
      (filter === 'all' || card.value.status === filter) &&
      `${card.value.question} ${card.value.answer} ${card.value.source}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );

  const currentPage = Math.min(page, Math.max(0, Math.ceil(matches.length / 20) - 1));

  useEffect(() => {
    let cancelled = false;

    setAvailable(false);

    if (!offline) {
      void request<{ available: boolean }>('/api/learning/config')
        .then(result => {
          if (!cancelled) {
            setAvailable(result.available);
          }
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [offline]);

  async function perform(action: () => Promise<void>) {
    if (locked.current) {
      return;
    }

    locked.current = true;
    setBusy(true);
    setError('');

    try {
      await action();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }

  async function start() {
    const instant = Date.now(),
      state = learningDay(workspace.snapshot().records, instant);

    if (
      state.active ||
      state.remainingMs <= 0 ||
      !learningQueue(workspace.snapshot().records, instant).queue.length
    ) {
      return;
    }

    await workspace.save(crypto.randomUUID(), 'learning-session', {
      day: state.day,
      startedAt: new Date(instant).toISOString(),
      deadline: new Date(instant + state.remainingMs).toISOString(),
      endedAt: null,
      reviewed: 0,
    });

    setRevealed('');
    setMessage('');
  }

  async function end() {
    const session = learningDay(workspace.snapshot().records).active;

    if (session) {
      await workspace.save(session.id, 'learning-session', {
        ...session.value,
        endedAt: new Date(Math.min(Date.now(), Date.parse(session.value.deadline))).toISOString(),
      });
    }

    setMessage('Review finished. Your remaining cards will be here when you return.');
    setRevealed('');
  }

  async function rate(rating: LearningRating) {
    const instant = Date.now(),
      state = workspace.snapshot().records;

    const session = learningDay(state, instant).active;
    const latest = learningCards(state).find(r => r.id === current?.id);

    if (!session || !latest || !showingAnswer) {
      return;
    }

    if (
      latest.value.lastReviewedAt !== current?.value.lastReviewedAt ||
      latest.value.question !== current?.value.question ||
      latest.value.answer !== current?.value.answer
    ) {
      setRevealed('');

      throw new Error('This card changed in another window. Please review it again.');
    }

    const updated = rateLearningCard(latest.value, rating, instant);
    const nextRecords = state.map(r => (r.id === latest.id ? { ...r, value: updated } : r));
    const nextQueue = learningQueue(nextRecords, instant);

    const waitingSoon = learningCards(nextRecords).some(
      r =>
        r.value.status === 'active' &&
        r.value.introducedAt &&
        Date.parse(r.value.dueAt) > instant &&
        Date.parse(r.value.dueAt) <= instant + 60_000,
    );

    const finished = !nextQueue.queue.length && !waitingSoon;

    const nextSession: LearningSession = {
      ...session.value,
      reviewed: session.value.reviewed + 1,
      endedAt: finished ? new Date(instant).toISOString() : null,
    };

    await workspace.saveMany([
      { id: latest.id, kind: 'learning-card', value: updated },
      { id: session.id, kind: 'learning-session', value: nextSession },
    ]);

    setRevealed('');

    if (finished) {
      setMessage('You’ve finished the cards available now. Your review is complete.');
    }
  }

  async function changeStatus(card: CardRow, status: LearningCard['status']) {
    const currentCard = learningCards(workspace.snapshot().records).find(r => r.id === card.id);

    if (!currentCard) {
      throw new Error('This card is unavailable.');
    }

    await workspace.save(card.id, 'learning-card', { ...currentCard.value, status });
    setRevealed('');
  }

  function controls(card: CardRow) {
    return (
      <div className="button-row wrap">
        <IconButton label="Edit card" disabled={busy} onClick={() => setEditing(card)}>
          <Pencil />
        </IconButton>
        {card.value.status === 'active' ? (
          <>
            <Button
              disabled={busy}
              onClick={() => void perform(() => changeStatus(card, 'suspended'))}
            >
              Suspend
            </Button>
            <ActionIconButton
              action="drop"
              label="Retire card"
              disabled={busy}
              onClick={() => void perform(() => changeStatus(card, 'retired'))}
            />
          </>
        ) : (
          <ActionIconButton
            action="restore"
            label="Resume card"
            disabled={busy}
            onClick={() => void perform(() => changeStatus(card, 'active'))}
          />
        )}
      </div>
    );
  }

  const nextDue = cards
    .filter(
      r => r.value.status === 'active' && r.value.introducedAt && Date.parse(r.value.dueAt) > now,
    )
    .sort((a, b) => a.value.dueAt.localeCompare(b.value.dueAt))[0];

  return (
    <main className="learning-page">
      <PageHeader
        eyebrow="MAKE IT STICK"
        title="Learning"
        description="A place for what you want to remember. Review after planning your day."
        actions={
          !active && (
            <ActionIconButton
              action="add"
              label="Learn something"
              onClick={() => setCapture(true)}
            />
          )
        }
      />
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {message && !active && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      {active ? (
        <section className="detail-card learning-review" aria-label="Morning review">
          <div className="section-heading">
            <h2>Morning recall</h2>
            <span className="muted">
              {today.reviewed} answered today ·{' '}
              {duration(Math.min(today.remainingMs, Date.parse(active.value.deadline) - now))} left
            </span>
          </div>
          {current ? (
            <>
              <p className="eyebrow">{current.value.introducedAt ? 'RECALL' : 'NEW CARD'}</p>
              <p className="learning-question">{current.value.question}</p>
              {showingAnswer ? (
                <>
                  <div className="learning-answer">
                    <h3>Reference answer</h3>
                    <p className="learning-text">{current.value.answer}</p>
                    {current.value.additions && (
                      <p className="notice learning-text">{current.value.additions}</p>
                    )}
                  </div>
                  <p className="muted">How well did you remember?</p>
                  <div className="button-row wrap learning-ratings">
                    {(['again', 'hard', 'good', 'easy'] as const).map(rating => {
                      const next = rateLearningCard(current.value, rating, now);

                      return (
                        <Button
                          key={rating}
                          disabled={busy}
                          variant={rating === 'good' ? 'primary' : 'secondary'}
                          onClick={() => void perform(() => rate(rating))}
                        >
                          {{ again: 'Again', hard: 'Hard', good: 'Good', easy: 'Easy' }[rating]} ·{' '}
                          {rating === 'again' ? '1 min' : `${next.intervalDays}d`}
                        </Button>
                      );
                    })}
                  </div>
                  <p className="muted">
                    Again: missed it. Hard: recalled with effort. Good: recalled correctly. Easy:
                    immediate recall.
                  </p>
                  <Disclosure summary="Original capture and note">
                    <p className="learning-text">{current.value.source || 'Written manually.'}</p>
                    {current.value.noteId &&
                      records.some(
                        r =>
                          r.id === current.value.noteId &&
                          r.kind === 'note' &&
                          !r.deleted &&
                          !(r.value as Note).trashed,
                      ) && (
                        <RouteLink href={notePath(current.value.noteId)}>
                          Open linked note
                        </RouteLink>
                      )}
                  </Disclosure>
                </>
              ) : (
                <>
                  <p className="muted">Answer mentally or aloud before revealing.</p>
                  <Button variant="primary" disabled={busy} onClick={() => setRevealed(revealKey)}>
                    Reveal answer
                  </Button>
                </>
              )}
              {current.value.lapses >= 3 && (
                <p className="notice">
                  This card has been difficult several times. Consider a smaller question, or
                  suspend it while you clarify the idea.
                </p>
              )}
              {controls(current)}
            </>
          ) : (
            <div role="status">
              <h3>A moment to breathe</h3>
              <p>
                {nextDue && Date.parse(nextDue.value.dueAt) <= Date.parse(active.value.deadline)
                  ? `Your next card is ready in ${duration(Date.parse(nextDue.value.dueAt) - now)}. You can finish here too.`
                  : 'No more cards are available now. You can finish your session.'}
              </p>
            </div>
          )}
          <div className="button-row wrap">
            <p className="muted">
              The timer continues until you end the review, even if you leave this page.
            </p>
            <Button disabled={busy} onClick={() => void perform(end)}>
              End review
            </Button>
          </div>
        </section>
      ) : (
        <>
          <section className="detail-card learning-overview">
            <div>
              <h2>{today.remainingMs === 0 ? 'Enough for today' : 'Your morning review'}</h2>
              <p className="muted">
                {plan.reviews.length} due · {plan.queue.length - plan.reviews.length} new available
                · {today.reviewed} answered today
              </p>
              <p>
                {today.remainingMs === 0
                  ? 'Your thirty-minute budget is complete. Remaining cards carry forward.'
                  : `${duration(today.remainingMs)} available today. Finish earlier whenever you need to.`}
              </p>
              {plan.waiting.length > 0 && (
                <p className="muted">
                  {plan.waiting.length} approved{' '}
                  {plan.waiting.length === 1 ? 'card waiting' : 'cards waiting'} to be introduced.
                  New cards slow down when reviews accumulate.
                </p>
              )}
            </div>
            <Button
              variant="primary"
              disabled={busy || today.remainingMs === 0 || plan.queue.length === 0}
              onClick={() => void perform(start)}
            >
              Start review
            </Button>
            {plan.queue.length === 0 && cards.length > 0 && today.remainingMs > 0 && (
              <p className="muted">
                Nothing to review right now.
                {nextDue
                  ? ` Next due: ${new Date(nextDue.value.dueAt).toLocaleString(undefined, { timeZone: calendarZone() })}.`
                  : ''}
              </p>
            )}
          </section>
          {cards.length === 0 ? (
            <div className="empty-state">
              <Brain size={36} />
              <h2>Keep something you learned</h2>
              <p>
                Capture a word, a useful fact, or an idea. Approve a card and find it here for your
                morning recall.
              </p>
              <ActionIconButton
                action="add"
                label="Learn something"
                onClick={() => setCapture(true)}
              />
            </div>
          ) : (
            <section className="learning-library" aria-label="Learning cards">
              <div className="learning-toolbar">
                <SearchField
                  label="Search learning cards"
                  placeholder="Search cards…"
                  value={search}
                  onChange={value => {
                    setSearch(value);
                    setPage(0);
                  }}
                />
                <div className="learning-filter">
                  <Select
                    aria-label="Card status"
                    value={filter}
                    onChange={e => {
                      setFilter(e.target.value);
                      setPage(0);
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="retired">Retired</option>
                    <option value="all">All cards</option>
                  </Select>
                </div>
              </div>
              {matches.length === 0 && (
                <div className="empty-state">
                  <Brain size={36} />
                  <h2>No matching cards</h2>
                  <p>Try another search or status.</p>
                </div>
              )}
              {matches.slice(currentPage * 20, (currentPage + 1) * 20).map(card => (
                <article className="detail-card learning-library-card" key={card.id}>
                  <div className="section-heading">
                    <h2>{card.value.question}</h2>
                    {controls(card)}
                  </div>
                  <p className="muted">
                    {card.value.status} ·{' '}
                    {card.value.introducedAt
                      ? `${card.value.reviews} ${card.value.reviews === 1 ? 'review' : 'reviews'} · due ${new Date(card.value.dueAt).toLocaleDateString(undefined, { timeZone: calendarZone() })}`
                      : 'Waiting for first review'}
                  </p>
                  <Disclosure summary="Answer and original capture">
                    <p className="learning-text">{card.value.answer}</p>
                    {card.value.additions && (
                      <p className="notice learning-text">{card.value.additions}</p>
                    )}
                    {card.value.source && (
                      <>
                        <h3>Original capture</h3>
                        <p className="learning-text">{card.value.source}</p>
                      </>
                    )}
                  </Disclosure>
                </article>
              ))}
              {matches.length > 20 && (
                <div className="button-row wrap">
                  <Button disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
                    Previous
                  </Button>
                  <span>
                    Page {currentPage + 1} of {Math.ceil(matches.length / 20)}
                  </span>
                  <Button
                    disabled={(currentPage + 1) * 20 >= matches.length}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </section>
          )}
        </>
      )}
      {(capture || editing) && (
        <LearningCapture
          records={records}
          available={available && !offline}
          editing={editing}
          onClose={() => {
            setCapture(false);
            setEditing(undefined);
            setRevealed('');
          }}
        />
      )}
    </main>
  );
}
